import React, { useState } from "react";
import {
  Box,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { DashboardCard } from "../../../../../shared/components";
import { fatigueService } from "../data/fatigueService";
import { useAsync } from "../data/useFatigueData";
import {
  METRIC_DEFINITIONS,
  NOT_MEDICAL,
  PILLARS,
  WHY_DERIVED,
} from "../data/methodology";
import { CohortRiskTable } from "../components/CohortRiskTable";
import { RecoveryStrainHeatmap } from "../components/RecoveryStrainHeatmap";
import { WellbeingActionMatrix } from "../components/WellbeingActionMatrix";
import { WearCompliancePanel } from "../components/WearCompliancePanel";
import { FatigueTrendChart } from "../components/FatigueTrendChart";
import { RiskTierBadge } from "../components/RiskTierBadge";
import { SectionHeading, ScanlineBanner } from "../components/primitives";
import type { DerivedMetricKey, FatigueAction } from "../data/types";

// ── Operations / Safety ─────────────────────────────────────────────────────

export const OperationsSection: React.FC<{ onOpenCohort: (id: string) => void }> = ({
  onOpenCohort,
}) => {
  const { data: cohorts, loading } = useAsync(() => fatigueService.getCohortSummaries());

  return (
    <Stack spacing={3}>
      <SectionHeading
        overline="Operations / Safety"
        title="Where is risk building now?"
        subtitle="Cohorts in the low-readiness / high-load quadrant are load–readiness misalignment hotspots."
      />
      <ScanlineBanner
        tier="red"
        title="Construction night crew — sustained critical fatigue"
        body="4 consecutive days of high LRM. FED 17% of workdays vs baseline. CDI 58 — consistent with a sustained night block. Schedule review recommended; do not add load."
      />

      <DashboardCard
        title="Recovery vs strain — cohort quadrant"
        subtitle="Each point is a cohort. The highlighted quadrant is the LRM hotspot."
        expandable={false}
      >
        {loading || !cohorts ? (
          <Skeleton variant="rounded" height={360} />
        ) : (
          <RecoveryStrainHeatmap cohorts={cohorts} onSelectCohort={onOpenCohort} />
        )}
      </DashboardCard>

      <DashboardCard
        title="Cohort comparison"
        subtitle="Site, shift, FED, LRM, CDI, PDC, trend and risk tier. Select a row for controls."
        expandable={false}
      >
        {loading || !cohorts ? (
          <Skeleton variant="rounded" height={220} />
        ) : (
          <CohortRiskTable rows={cohorts} onSelectCohort={onOpenCohort} />
        )}
      </DashboardCard>
    </Stack>
  );
};

// ── Cohort Risk ─────────────────────────────────────────────────────────────

const TIER_GUIDE = [
  { tier: "green" as const, text: "Low FED, low LRM, stable CDI. Maintain schedule; routine monitoring." },
  { tier: "amber" as const, text: "Rising FED or CDI drift. Early warning — adjust schedule/rest and monitor." },
  { tier: "red" as const, text: "Sustained high FED + LRM + CDI or high PDC. Do not add load; prioritise rest and controls." },
];

export const CohortRiskSection: React.FC<{ onOpenCohort: (id: string) => void }> = ({
  onOpenCohort,
}) => {
  const { data: cohorts, loading } = useAsync(() => fatigueService.getCohortSummaries());
  const { data: overview } = useAsync(() => fatigueService.getOverview());
  const ranked = cohorts
    ? [...cohorts].sort((a, b) => b.fedPer1000 - a.fedPer1000)
    : [];

  return (
    <Stack spacing={3}>
      <SectionHeading
        overline="Cohort Risk"
        title="Teams ranked by fatigue risk"
        subtitle="Ordered by fatigue exposure. Tiers below explain the recommended posture."
      />

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" } }}>
        {TIER_GUIDE.map((g) => (
          <DashboardCard key={g.tier} expandable={false}>
            <Stack spacing={1}>
              <RiskTierBadge tier={g.tier} />
              <Typography variant="body2" color="text.secondary">
                {g.text}
              </Typography>
            </Stack>
          </DashboardCard>
        ))}
      </Box>

      <DashboardCard title="Ranked cohorts" expandable={false}>
        {loading || !cohorts ? (
          <Skeleton variant="rounded" height={240} />
        ) : (
          <CohortRiskTable rows={ranked} onSelectCohort={onOpenCohort} />
        )}
      </DashboardCard>

      <DashboardCard
        title="Benchmark vs baseline"
        subtitle="Organisation-wide derived-indicator trend over the last 12 weeks."
        expandable={false}
      >
        {!overview ? (
          <Skeleton variant="rounded" height={280} />
        ) : (
          <FatigueTrendChart data={overview.trend} height={280} />
        )}
      </DashboardCard>
    </Stack>
  );
};

// ── Wellbeing Actions ───────────────────────────────────────────────────────

const statusChip = (status: FatigueAction["status"]) => {
  const map = {
    todo: { label: "To do", color: "default" as const },
    in_progress: { label: "In progress", color: "warning" as const },
    done: { label: "Done", color: "success" as const },
  };
  return map[status];
};

export const ActionsSection: React.FC = () => {
  const { data: actions, loading } = useAsync(() => fatigueService.getActions());
  const [selected, setSelected] = useState<string | null>(null);
  const selectedAction = actions?.find((a) => a.id === selected) ?? null;

  return (
    <Stack spacing={3}>
      <SectionHeading
        overline="Wellbeing Actions"
        title="What can we action now?"
        subtitle="Low-hanging-fruit recommendations ranked by impact and ease. Language is about support, rest and workload — not blame."
      />

      <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" } }}>
        <DashboardCard title="Impact / ease matrix" subtitle="Top-right are quick wins." expandable={false}>
          {loading || !actions ? (
            <Skeleton variant="rounded" height={360} />
          ) : (
            <WellbeingActionMatrix actions={actions} onSelectAction={setSelected} />
          )}
        </DashboardCard>

        <DashboardCard title="Action cards" subtitle="Owner, due date, status and notes." expandable={false}>
          {loading || !actions ? (
            <Skeleton variant="rounded" height={360} />
          ) : (
            <Stack spacing={1.5} sx={{ maxHeight: 400, overflowY: "auto" }}>
              {actions.map((a) => (
                <ActionCard
                  key={a.id}
                  action={a}
                  highlight={a.id === selected}
                  onClick={() => setSelected(a.id)}
                />
              ))}
            </Stack>
          )}
        </DashboardCard>
      </Box>

      {selectedAction && (
        <DashboardCard expandable={false}>
          <Stack spacing={1}>
            <Typography variant="h6" fontWeight={800}>
              {selectedAction.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedAction.description}
            </Typography>
          </Stack>
        </DashboardCard>
      )}
    </Stack>
  );
};

const ActionCard: React.FC<{
  action: FatigueAction;
  highlight: boolean;
  onClick: () => void;
}> = ({ action, highlight, onClick }) => {
  const theme = useTheme();
  const chip = statusChip(action.status);
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${highlight ? theme.palette.primary.main : theme.palette.divider}`,
        bgcolor: highlight ? alpha(theme.palette.primary.main, 0.06) : "transparent",
        cursor: "pointer",
        "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}` },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Typography variant="body2" fontWeight={700}>
          {action.title}
        </Typography>
        <Chip label={chip.label} size="small" color={chip.color} sx={{ height: 20, fontSize: 11 }} />
      </Stack>
      <Stack direction="row" spacing={2} sx={{ mt: 0.75 }}>
        <Typography variant="caption" color="text.secondary">
          Owner: {action.owner}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Due: {action.dueDate}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Impact {action.impact}/5 · Ease {action.ease}/5
        </Typography>
      </Stack>
      {action.notes && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, fontStyle: "italic" }}>
          {action.notes}
        </Typography>
      )}
    </Box>
  );
};

// ── Wear Compliance ─────────────────────────────────────────────────────────

export const WearComplianceSection: React.FC = () => {
  const { data, loading } = useAsync(() => fatigueService.getCompliance());

  return (
    <Stack spacing={3}>
      <SectionHeading
        overline="Wear Compliance"
        title="Device coverage & data confidence"
        subtitle="Where wearable data is incomplete, dashboard confidence is reduced and figures are indicative."
      />
      <DashboardCard expandable={false}>
        {loading || !data ? (
          <Skeleton variant="rounded" height={320} />
        ) : (
          <WearCompliancePanel rows={data} />
        )}
      </DashboardCard>
    </Stack>
  );
};

// ── Methodology ─────────────────────────────────────────────────────────────

export const MethodologySection: React.FC<{ onOpenMetric: (k: DerivedMetricKey) => void }> = ({
  onOpenMetric,
}) => {
  const keys: DerivedMetricKey[] = ["fed", "lrm", "cdi", "pdc"];
  return (
    <Stack spacing={3}>
      <SectionHeading
        overline="Methodology"
        title="The 3 pillars & derived indicators"
        subtitle="How fatigue exposure is modelled, and why PEAK4 uses its own operational metrics."
      />

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" } }}>
        {PILLARS.map((p) => (
          <DashboardCard key={p.title} expandable={false}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.5 }}>
              {p.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {p.body}
            </Typography>
          </DashboardCard>
        ))}
      </Box>

      <DashboardCard title="Derived indicators" subtitle="Select one for the full definition." expandable={false}>
        <Stack spacing={1.5}>
          {keys.map((k) => {
            const d = METRIC_DEFINITIONS[k];
            return (
              <Box
                key={k}
                role="button"
                tabIndex={0}
                onClick={() => onOpenMetric(k)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpenMetric(k);
                  }
                }}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: (t) => `1px solid ${t.palette.divider}`,
                  cursor: "pointer",
                  "&:hover": { borderColor: "primary.main" },
                  "&:focus-visible": { outline: (t) => `2px solid ${t.palette.primary.main}` },
                }}
              >
                <Typography variant="body2" fontWeight={700}>
                  {d.code} — {d.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {d.question}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </DashboardCard>

      <DashboardCard title="Why PEAK4-derived metrics?" expandable={false}>
        <Typography variant="body2" color="text.secondary">
          {WHY_DERIVED}
        </Typography>
      </DashboardCard>

      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          border: (t) => `1px solid ${alpha(t.palette.warning.main, 0.5)}`,
          bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
        }}
      >
        <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>
          Not a medical diagnosis
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {NOT_MEDICAL} PDC in particular is an early "something is off" signal, not a diagnostic tool.
        </Typography>
      </Box>
      <Divider />
    </Stack>
  );
};
